import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    ShoppingCart, Trash, ArrowLeft, Coins, Package, Plus, Minus, Check
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function Cart() {
    const navigate = useNavigate();
    const [cart, setCart] = useState({ items: [], total_boltz: 0, item_count: 0 });
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);

    const fetchCart = async () => {
        try {
            const res = await fetch(`${API}/cart`, { credentials: 'include', headers: getH() });
            if (res.ok) setCart(await res.json());
        } catch (e) { console.error('Cart:', e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchCart(); }, []);

    const removeItem = async (itemId) => {
        try {
            const res = await fetch(`${API}/cart/${itemId}`, { method: 'DELETE', credentials: 'include', headers: getH() });
            if (res.ok) { fetchCart(); toast.success('Removed'); }
        } catch { toast.error('Failed'); }
    };

    const checkout = async () => {
        setChecking(true);
        try {
            const res = await fetch(`${API}/cart/checkout`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Purchased ${data.orders_created} items for ${data.total_charged} Boltz!`);
                setCart({ items: [], total_boltz: 0, item_count: 0 });
                navigate('/orders');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Checkout failed');
            }
        } catch { toast.error('Network error'); }
        finally { setChecking(false); }
    };

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <Button size="sm" variant="ghost" onClick={() => navigate('/shop')} className="mb-2 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Shop
                    </Button>
                    <span className="label-text text-primary text-glow">CART</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="cart-title">
                        Shopping Cart
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">{cart.item_count} items</p>
                </motion.div>

                {loading ? (
                    <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
                ) : cart.items.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-8 text-center">
                            <ShoppingCart size={48} className="text-muted-foreground mx-auto mb-4" />
                            <p className="font-medium text-foreground">Cart is empty</p>
                            <p className="text-sm text-muted-foreground mt-1">Add items from the shop to get started</p>
                            <Button onClick={() => navigate('/shop')} className="mt-4 btn-primary">Browse Shop</Button>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="space-y-3 mb-6">
                            <AnimatePresence>
                                {cart.items.map(ci => (
                                    <motion.div key={ci.cart_item_id || ci.item_id}
                                        initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 100 }}>
                                        <Card className="glass-card" data-testid={`cart-item-${ci.item_id}`}>
                                            <CardContent className="p-3 flex items-center gap-3">
                                                <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted shrink-0 cursor-pointer"
                                                    onClick={() => navigate(`/shop/item/${ci.item_id}`)}>
                                                    {ci.item?.image_url ? (
                                                        <img src={ci.item.image_url} alt={ci.item.name} className="w-full h-full object-cover" />
                                                    ) : <div className="w-full h-full flex items-center justify-center"><Package size={24} className="text-muted-foreground" /></div>}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-bold text-sm text-foreground truncate">{ci.item?.name}</p>
                                                    <div className="flex gap-2 text-[10px] text-muted-foreground mt-0.5">
                                                        {ci.size && <Badge variant="outline" className="text-[9px]">{ci.size}</Badge>}
                                                        {ci.color && <Badge variant="outline" className="text-[9px]">{ci.color}</Badge>}
                                                        <span>x{ci.quantity}</span>
                                                    </div>
                                                    <p className="font-mono text-neon-gold text-sm mt-1">{ci.line_total?.toLocaleString()} Boltz</p>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => removeItem(ci.item_id)}
                                                    className="text-muted-foreground hover:text-plasma-red shrink-0">
                                                    <Trash size={16} />
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* Checkout Summary */}
                        <Card className="glass-card border-neon-gold/20">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-muted-foreground">Total</span>
                                    <span className="text-2xl font-black font-mono text-neon-gold" data-testid="cart-total">
                                        {cart.total_boltz?.toLocaleString()} Boltz
                                    </span>
                                </div>
                                <Button onClick={checkout} disabled={checking} className="w-full btn-primary text-base py-3"
                                    data-testid="checkout-btn">
                                    {checking ? <><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />Processing...</> : <><Check size={20} className="mr-2" />Checkout ({cart.item_count} items)</>}
                                </Button>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}
